<template>
    <BaseLayout :fill-height="isGeneratingProfile">
        <p class="sr-only" aria-live="polite" aria-atomic="true">{{ isGeneratingProfile ? "Regenerating your collaboration profile." : "" }}</p>
        <LoadingStep v-if="isGeneratingProfile" class="grow" has-perspective main-loading-message="Regenerating your collaboration profile" />
        <div v-else class="max-w-xl mx-auto w-full">
            <button
                v-if="!hasInProgressSession"
                class="mb-4 inline-flex items-center gap-1.5 body-small text-gray-400 hover:text-gray-600 hover:underline transition-colors"
                :disabled="isRetaking"
                @click="() => executeRetake()">
                <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                Retake Assessment
            </button>
            <!-- In-progress banner -->
            <div v-if="hasInProgressSession" role="status" class="mb-8 rounded-lg border border-valence-primary-200 bg-valence-primary-50 px-4 py-3 text-center">
                <p class="body-small text-gray-700">You opted to retake this assessment. Pick up where you left off.</p>
                <div class="flex items-center justify-center gap-4 mt-2">
                    <button class="body-small-semibold text-valence-primary-600 hover:underline" :disabled="isCancelling" @click="router.visit('/perspective/questions/')">
                        Continue
                    </button>
                    <button class="body-small text-gray-400 hover:text-gray-600 hover:underline transition-colors" :disabled="isCancelling" @click="() => executeCancelRetake()">
                        Cancel Retake
                    </button>
                </div>
            </div>

            <!-- Hero -->
            <div class="text-center mb-3">
                <ArchetypeDot :letters="result.letters" class="mx-auto mb-4 size-28 max-md:size-21" />
                <p class="body text-valence-grey-750 mb-1">You're {{ article }}</p>
                <h1 class="display-medium text-valence-grey-850">{{ archetype }}</h1>
            </div>

            <!-- Description -->
            <p v-if="typeData" class="nadia-chat text-[#595959] mb-10">
                {{ typeData.shortDescription }}
            </p>

            <!-- Dimension bars -->
            <div class="space-y-6 mb-10">
                <div v-for="dim in result.dimensions" :key="dim.code">
                    <p class="body-small text-gray-700 mb-2">{{ dimensionTitle(dim.code, dim.binned_score) }}</p>
                    <DimensionBar
                        :code="dim.code"
                        :binned-score="dim.binned_score"
                        :left-label="dimensionLabel(dim.code, 'left')"
                        :right-label="dimensionLabel(dim.code, 'right')" />
                </div>
            </div>

            <!-- Actions -->
            <div class="text-center">
                <div class="inline-flex items-center gap-2">
                    <BaseLink
                        v-if="!collaboration_profile_enabled"
                        href="/accounts/user-profile#tab=archetype"
                        class="base-btn-primary inline-block px-8 py-2.5 body-small-semibold">
                        Explore Archetype
                    </BaseLink>
                    <BaseButton v-else-if="has_collaboration_profile" theme="primary" class="px-8 py-2.5 body-small-semibold" @click="handleRegenerateProfile">
                        Regenerate Collaboration Profile
                    </BaseButton>
                    <BaseLink v-else href="/profile/create?auto_generate=true" class="base-btn-primary inline-block px-8 py-2.5 body-small-semibold">
                        Generate Collaboration Profile
                    </BaseLink>
                    <CollaborationProfileTooltip v-if="collaboration_profile_enabled" :regeneration="has_collaboration_profile" />
                </div>
                <p v-if="generationError" role="alert" class="body-small text-red-700 mt-3">{{ generationError }}</p>
                <div class="mt-3">
                    <BaseLink href="/coach?script=perspective&chat_source=perspective_assessment" class="body-small text-valence-primary-600 hover:underline"
                        >Chat with Nadia</BaseLink
                    >
                </div>
            </div>
        </div>
        <PerspectiveDemoTools v-if="!isGeneratingProfile" />
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import BaseLink from "~vue/router/BaseLink.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import CollaborationProfileTooltip from "~vue/CollaborationProfile/CollaborationProfileTooltip.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import LoadingStep from "~vue/components/ProfileWizard/LoadingStep.vue";
import { useProfileWizard } from "~vue/composables/useProfileWizard";
import type { Dimension, PerspectiveResult } from "~vue/pages/perspectiveTypes";
import { DIMENSION_LABELS, PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import PerspectiveDemoTools from "~vue/Perspective/PerspectiveDemoTools.vue";
import DimensionBar from "~vue/UserProfile/DimensionBar.vue";
import { articleFor } from "~vue/utils/stringUtils";
import { computed } from "vue";

export interface Props {
    result: PerspectiveResult;
    in_progress_session_id: string | null;
    collaboration_profile_enabled: boolean;
    has_collaboration_profile: boolean;
    is_manager: boolean;
}

const props = defineProps<Props>();

const hasInProgressSession = computed(() => props.in_progress_session_id != null);

const { generateProfile, generationError, isGeneratingProfile } = useProfileWizard({
    isManager: props.is_manager,
    hasPerspectiveReport: true,
});

async function handleRegenerateProfile(): Promise<void> {
    await generateProfile({
        fileIds: [],
        isRecreating: true,
        regenerateFromPerspective: true,
    });
    if (!generationError.value) {
        router.visit("/accounts/collaboration-profile");
    }
}

const { execute: executeRetake, isFetching: isRetaking, onFetchResponse: onRetakeResponse } = useFetch("/v1/perspectives/sessions", { immediate: false }).post({}).json();

onRetakeResponse(() => {
    router.visit("/perspective/questions/");
});

const cancelUrl = computed(() => (props.in_progress_session_id ? `/v1/perspectives/sessions/${props.in_progress_session_id}` : ""));

const { execute: executeCancelRetake, isFetching: isCancelling, onFetchResponse: onCancelResponse } = useFetch(cancelUrl, { immediate: false }).delete();

onCancelResponse(() => {
    router.visit("/perspective/");
});

const typeData = computed(() => PERSPECTIVE_TYPES[props.result.letters] ?? null);
const archetype = computed(() => typeData.value?.title ?? props.result.archetype);
const article = computed(() => articleFor(archetype.value));

function dimensionLabel(code: Dimension, side: "left" | "right"): string {
    return DIMENSION_LABELS[code][side];
}

function dimensionTitle(code: Dimension, binnedScore: number): string {
    const left = dimensionLabel(code, "left");
    const right = dimensionLabel(code, "right");
    if (binnedScore === 0) {
        return `You tend to be balanced between ${left} and ${right}.`;
    }
    const dominant = binnedScore > 0 ? right : left;
    const other = binnedScore > 0 ? left : right;
    const abs = Math.abs(binnedScore);
    const strength = abs === 1 ? "slightly more" : abs === 2 ? "more" : "strongly more";
    return `You tend to be ${strength} ${dominant} than ${other}.`;
}
</script>
